// /src/views/superAdmin/events/eventReceiptHtmlTemplates/components/eventReceiptTemplates/common/header.ts

/**
 * Reusable header components for email templates
 * Contains different header styles that can be used across templates
 */

// Standard header with logo and title
export const STANDARD_HEADER = (
  title: string,
  subtitle: string = "",
  bgColor: string = "#1a2310"
) => `
<mj-section background-color="${bgColor}" padding="20px 0">
  <mj-column>
    <mj-image src="https://www.copilotly.com/img/logo/copilotly-logo.png" alt="Copilotly Logo" width="200px" padding-bottom="10px" />
    <mj-text font-size="30px" color="#ffffff" font-family="Arial, sans-serif" align="center" font-weight="bold">${title}</mj-text>
    ${
      subtitle
        ? `<mj-text font-size="16px" color="#99CC66" font-family="Arial, sans-serif" align="center">${subtitle}</mj-text>`
        : ""
    }
  </mj-column>
</mj-section>
`;

// Event header with prominent background and event details
export const EVENT_HEADER = (
  title: string,
  subtitle: string = "",
  bgColor: string = "#336633"
) => `
<mj-section background-color="${bgColor}" padding="20px 0">
  <mj-column>
    <mj-image src="https://www.copilotly.com/img/logo/copilotly-logo.png" alt="Copilotly Logo" width="200px" padding-bottom="10px" />
    <mj-text font-size="32px" color="#ffffff" font-family="Arial, sans-serif" align="center" font-weight="bold">${title}</mj-text>
    ${
      subtitle
        ? `<mj-text font-size="18px" color="#99CC66" font-family="Arial, sans-serif" align="center">${subtitle}</mj-text>`
        : ""
    }
  </mj-column>
</mj-section>
`;

// Transactional header for confirmations and receipts
export const TRANSACTIONAL_HEADER = (title: string, subtitle: string = "") => `
<mj-section background-color="#1a2310" padding="20px 0">
  <mj-column>
    <mj-image src="https://www.copilotly.com/img/logo/copilotly-logo.png" alt="Copilotly Logo" width="200px" padding-bottom="10px" />
    <mj-text font-size="30px" color="#ffffff" font-family="Arial, sans-serif" align="center" font-weight="bold">${title}</mj-text>
    ${
      subtitle
        ? `<mj-text font-size="16px" color="#99CC66" font-family="Arial, sans-serif" align="center">${subtitle}</mj-text>`
        : ""
    }
  </mj-column>
</mj-section>
`;

// Marketing header with lighter background for promotional content
export const MARKETING_HEADER = (
  title: string,
  subtitle: string = "",
  bgColor: string = "#1e3c19"
) => `
<mj-section background-color="${bgColor}" padding="20px 0">
  <mj-column>
    <mj-image src="https://www.copilotly.com/img/logo/copilotly-logo.png" alt="Copilotly Logo" width="200px" padding-bottom="10px" />
    <mj-text font-size="30px" color="#ffffff" font-family="Arial, sans-serif" align="center" font-weight="bold">${title}</mj-text>
    ${
      subtitle
        ? `<mj-text font-size="16px" color="#99CC66" font-family="Arial, sans-serif" align="center">${subtitle}</mj-text>`
        : ""
    }
  </mj-column>
</mj-section>
`;

// Onboarding header with welcoming design for new users
export const ONBOARDING_HEADER = (
  title: string,
  subtitle: string = "",
  bgColor: string = "#274d1c"
) => `
<mj-section background-color="${bgColor}" padding="20px 0">
  <mj-column>
    <mj-image src="https://www.copilotly.com/img/logo/copilotly-logo.png" alt="Copilotly Logo" width="200px" padding-bottom="10px" />
    <mj-text font-size="30px" color="#ffffff" font-family="Arial, sans-serif" align="center" font-weight="bold">${title}</mj-text>
    ${
      subtitle
        ? `<mj-text font-size="16px" color="#99CC66" font-family="Arial, sans-serif" align="center">${subtitle}</mj-text>`
        : ""
    }
  </mj-column>
</mj-section>
`;

// Product header with a focus on features and benefits
export const PRODUCT_HEADER = (
  title: string,
  subtitle: string = "",
  bgColor: string = "#1a2310"
) => `
<mj-section background-color="${bgColor}" padding="20px 0">
  <mj-column>
    <mj-image src="https://www.copilotly.com/img/logo/copilotly-logo.png" alt="Copilotly Logo" width="200px" padding-bottom="10px" />
    <mj-text font-size="30px" color="#ffffff" font-family="Arial, sans-serif" align="center" font-weight="bold">${title}</mj-text>
    ${
      subtitle
        ? `<mj-text font-size="16px" color="#99CC66" font-family="Arial, sans-serif" align="center">${subtitle}</mj-text>`
        : ""
    }
  </mj-column>
</mj-section>
`;

// Pulse/Report header with data-focused design
export const PULSE_HEADER = (
  title: string,
  subtitle: string = "",
  bgColor: string = "#1a1a2e"
) => `
<mj-section background-color="${bgColor}" padding="30px 0">
  <mj-column>
    <mj-image src="https://www.copilotly.com/img/logo/copilotly-logo.png" alt="Copilotly Logo" width="160px" padding-bottom="15px" />
    <mj-text font-size="28px" color="#ffffff" font-family="Arial, sans-serif" align="center" font-weight="bold">${title}</mj-text>
    ${
      subtitle
        ? `<mj-text font-size="14px" color="#64748b" font-family="Arial, sans-serif" align="center" padding-top="5px">${subtitle}</mj-text>`
        : ""
    }
  </mj-column>
</mj-section>
`;

// Alert header with urgent design for time-sensitive messages
export const ALERT_HEADER = (
  title: string,
  subtitle: string = "",
  bgColor: string = "#f97316"
) => `
<mj-section background-color="${bgColor}" padding="25px 0">
  <mj-column>
    <mj-text font-size="32px" color="#ffffff" font-family="Arial, sans-serif" align="center" font-weight="bold">${title}</mj-text>
    ${
      subtitle
        ? `<mj-text font-size="16px" color="#ffffff" font-family="Arial, sans-serif" align="center" padding-top="5px">${subtitle}</mj-text>`
        : ""
    }
  </mj-column>
</mj-section>
`;
